// A small seeded generator, so a match can be rebuilt from a shared code.
//
// Only the things that decide what happens need to be reproducible: the city and
// the wind. Building sizes fix where the gorillas stand, how high they perch and
// which windows a banana can sail through, so two players who share a code have
// to get identical buildings or the duel is not fair.
//
// Everything else — which antic a gorilla fancies, where the smoke drifts, when
// the station next crosses — is deliberately left on Math.random(). A shared
// match transmits its *results*, it does not re-simulate them, so the two
// players never need the same twinkling star.

/**
 * mulberry32. Thirty-two bits of state, no dependencies, and a sequence that is
 * plenty good for laying out a city.
 */
function mulberry32(seed) {
  let a = seed >>> 0;

  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a. Turns a readable code into the 32-bit number the generator wants. */
export function seedFrom(text) {
  let h = 0x811c9dc5;

  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }

  return h >>> 0;
}

/** Six characters that are easy to read out loud and to type back in. */
export const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** A fresh, human-readable seed like "K7M2QP". */
export function randomSeed() {
  let out = '';

  for (let i = 0; i < 6; i++) {
    out += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }

  return out;
}

/** The code that gets shared, once it is a number again. */
export function seedText(seed) {
  return String(seed);
}

let next = mulberry32(0);
let current = 0;

/**
 * Points the generator at a new match. Anything generated from here on — until
 * the next reseed — is identical for anyone holding the same seed.
 */
export function reseed(seed) {
  current = typeof seed === 'number' ? seed >>> 0 : seedFrom(String(seed));
  next = mulberry32(current);
  return current;
}

/** The seed currently in play, for putting in a match code. */
export function activeSeed() {
  return current;
}

/** Like utils.rand, but reproducible: same seed, same sequence, every time. */
export function matchRand(a, b) {
  return a + Math.floor(next() * (b - a + 1));
}

/**
 * A private stream for randomness that has to depend only on its own inputs.
 *
 * Craters use this: the chips around a hole must come out the same every time it
 * is cut, however many times that happens, or the same crater would be a
 * different shape on the other player's machine and a banana would sail through
 * a gap that is not there.
 */
export function localStream(...parts) {
  return mulberry32(seedFrom(parts.join(',')));
}
